import type { KeyboardEvent } from 'react';
import React, { useCallback, useMemo } from 'react';

import { css } from '@emotion/react';
import { Button } from '@rtkwlf/fenrir-react';
import type { TicketWithComments } from '@rtkwlf/rendall';
import { CreateTicketInputTicketType, TicketStatus } from '@rtkwlf/rendall';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { NewTicketInfo } from '@packages/components/contact/contact-concierge-link';
import { useSelectedOrganization } from '@packages/legacy/organization-context';
import { DEFAULT_TAB_INDEX, EventKey } from 'ae-constants/constants';
import {
  CONTACT_CONCIERGE_ROUTE,
  TICKETS_ROUTE,
} from 'ae-constants/routes.constants';

type ButtonProps = {
  ticket: TicketWithComments;
};

export const FollowUpButton: React.FC<ButtonProps> = ({ ticket }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: orgId } = useSelectedOrganization();
  const ticketId = ticket.id;
  const currentUrl = useMemo(
    () => `${window.location.origin}${TICKETS_ROUTE}/${ticketId}`,
    [ticketId],
  );

  const initialInfo = useMemo<NewTicketInfo>(
    () => ({
      description: `${t('ticket:followUp', { currentUrl })}\n\n`,
      link: `${currentUrl}?id=${orgId}`,
      relatedTicket: ticketId,
      subject: `${t('ticket:prePostSubject', { ticketId })}`,
      ticketType: CreateTicketInputTicketType.General,
    }),
    [currentUrl, orgId, t, ticketId],
  );

  const handleClick = useCallback(() => {
    navigate(CONTACT_CONCIERGE_ROUTE, { state: { initialInfo } });
  }, [navigate, initialInfo]);

  const handleKeyUp = useCallback(
    (e: KeyboardEvent): void => {
      if (e.key === EventKey.Return) {
        handleClick();
      }
    },
    [handleClick],
  );

  if (ticket.status === TicketStatus.Closed) {
    return (
      <Button
        variant="secondary"
        aria-label={t('ticket:postFollowUp')}
        onKeyUp={handleKeyUp}
        tabIndex={DEFAULT_TAB_INDEX}
        onClick={handleClick}
        css={css({
          minHeight: '2rem',
        })}
      >
        {t('ticket:postFollowUp')}
      </Button>
    );
  }
  return null;
};
